import { Link } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { ArrowUpRight, ArrowRight, ClipboardList, UserRoundCheck, CalendarDays, Heart, ShieldCheck, CheckCircle2 } from "lucide-react";
import Navigation from "../components/Navigation";
import Footer from "../components/Footer";
import SEO from "../components/SEO";
import JsonLd from "../components/JsonLd";
import DariusMatchCard from "../components/DariusMatchCard";
import { useSurveyUrl } from "../hooks/useSurveyUrl";
import { trackSurveyStart } from "../lib/track";
import { fontSans, fontMono, color as T, radius } from "../design/tokens";

// Homepage — visual-treatment revision (2026-02-28).
//
// Hero composition is APPROVED and preserved. The only hero change is
// the reduced H1 scale specified by the design brief:
//     clamp(2.125rem, 2.7vw, 3rem) / line-height 1.03 / -0.035em
//
// Below the hero, each section now carries a distinct cool-tinted
// surface (blue, mint, lavender, cool neutral, ink navy) to establish
// visual rhythm. Section H2s use the reduced scale
//     clamp(1.5rem, 1.9vw, 2rem) / line-height 1.1 / -0.025em
// The "N /" sequence prefix is retired site-wide on the homepage. The
// How-It-Works block uses plain 01–04 in Geist Mono + teal. Body copy
// sizing is unchanged.
//
// No copy, CTA destinations, tracking, survey routing, integrations,
// or interior pages are modified.

// ── Persona story imagery (reused unchanged) ────────────────────────
const PERSONA_STORIES = [
  {
    src: "https://customer-assets.emergentagent.com/job_8e1914ff-fb25-4b50-9af6-080e8f865a43/artifacts/akvzth1c_9.png",
    alt:
      "Emma (Monogamous, Heterosexual). Done with swiping and small talk; looking for something traditional built on chemistry, trust, and real connection.",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_8e1914ff-fb25-4b50-9af6-080e8f865a43/artifacts/p188cijq_1.png",
    alt:
      "Taylor (31, Bisexual). Loves connecting with couples but is done being treated like a fantasy. Wants genuine interest, not objectification.",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_8e1914ff-fb25-4b50-9af6-080e8f865a43/artifacts/uai7kdd6_2.png",
    alt:
      "David (34, Heteroflexible). Not chasing commitment, just connection. With couples he finds warmth, excitement, and room to explore attraction.",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_8e1914ff-fb25-4b50-9af6-080e8f865a43/artifacts/i1jw2gsl_6.png",
    alt:
      "Alex & Jordan (Married & Monogamish). Committed but occasionally invite another man into their world — a space for Alex's bi curiosity and their shared connection.",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_8e1914ff-fb25-4b50-9af6-080e8f865a43/artifacts/1te1bgl3_7.png",
    alt:
      "James (38, Monogamous, Bisexual). Wants commitment with one person, not suspicion. Ready to stop shrinking himself to make others comfortable.",
  },
];
const STORY_INTERVAL_MS = 11000;
const STORY_FADE_MS = 1400;

// ── How-It-Works step illustrations ─────────────────────────────────
// Each mini-illustration is a small piece of "product surface" the
// visitor can recognise: a survey answer, a compatibility read, a
// Match Monday day marker, a mutual-connection state. Built with
// SVG + inline blocks — no external images.

function SurveyIllustration({ active = true, delay = 0 }) {
  const enter = {
    opacity: active ? 1 : 0,
    transform: active ? "translateY(0)" : "translateY(6px)",
    transition: `opacity 480ms ease ${delay}ms, transform 620ms cubic-bezier(0.22, 1, 0.36, 1) ${delay}ms`,
  };
  return (
    <div className="absolute inset-0 flex items-center justify-center p-5">
      <div
        style={{
          width: "100%",
          maxWidth: 240,
          background: "#FFFFFF",
          borderRadius: 10,
          border: `1px solid ${T.line}`,
          boxShadow: "0 8px 18px -12px rgba(11,18,32,0.10)",
          padding: 14,
          ...enter,
        }}
      >
        <div
          style={{
            fontFamily: fontMono,
            fontSize: 9,
            letterSpacing: "0.20em",
            fontWeight: 700,
            color: T.inkMuted,
            textTransform: "uppercase",
          }}
        >
          Question 04 · 24
        </div>
        <div
          className="mt-1.5"
          style={{
            fontFamily: fontSans,
            fontSize: 12,
            fontWeight: 500,
            color: T.ink,
            letterSpacing: "-0.005em",
            lineHeight: 1.3,
          }}
        >
          What matters most to you?
        </div>
        <div className="mt-2.5 flex flex-wrap gap-1.5">
          {[
            { t: "Commitment", on: true },
            { t: "Honesty", on: false },
            { t: "Chemistry", on: false },
          ].map((o) => (
            <span
              key={o.t}
              style={{
                padding: "4px 8px",
                borderRadius: 999,
                fontFamily: fontSans,
                fontSize: 10,
                fontWeight: 500,
                background: o.on ? T.accent : "#FFFFFF",
                color: o.on ? "#FFFFFF" : T.ink,
                border: `1px solid ${o.on ? T.accent : T.line}`,
              }}
            >
              {o.t}
            </span>
          ))}
        </div>
        <div
          className="mt-3"
          style={{
            height: 3,
            background: "#E7ECEF",
            borderRadius: 999,
            position: "relative",
            overflow: "hidden",
          }}
        >
          <div
            style={{
              width: active ? "16%" : "0%",
              height: "100%",
              background: T.accent,
              borderRadius: 999,
              transition: `width 900ms cubic-bezier(0.22, 1, 0.36, 1) ${delay + 240}ms`,
            }}
          />
        </div>
      </div>
    </div>
  );
}

function CompatIllustration({ active = true, delay = 0 }) {
  const rows = [
    { label: "Goals", pct: 91, color: "#16835D" },
    { label: "Structure", pct: 96, color: "#16835D" },
    { label: "Emotional", pct: 84, color: "#16835D" },
  ];
  const enter = {
    opacity: active ? 1 : 0,
    transform: active ? "translateY(0)" : "translateY(6px)",
    transition: `opacity 480ms ease ${delay}ms, transform 620ms cubic-bezier(0.22, 1, 0.36, 1) ${delay}ms`,
  };
  return (
    <div className="absolute inset-0 flex items-center justify-center p-5">
      <div
        style={{
          width: "100%",
          maxWidth: 240,
          background: "#FFFFFF",
          borderRadius: 10,
          border: `1px solid ${T.line}`,
          boxShadow: "0 8px 18px -12px rgba(11,18,32,0.10)",
          padding: 14,
          ...enter,
        }}
      >
        <div
          style={{
            fontFamily: fontMono,
            fontSize: 9,
            letterSpacing: "0.20em",
            fontWeight: 700,
            color: T.inkMuted,
            textTransform: "uppercase",
          }}
        >
          Compatibility Read
        </div>
        <div className="mt-2 space-y-2">
          {rows.map((r) => (
            <div key={r.label}>
              <div className="flex items-baseline justify-between">
                <span
                  style={{
                    fontFamily: fontSans,
                    fontSize: 11,
                    fontWeight: 500,
                    color: T.ink,
                  }}
                >
                  {r.label}
                </span>
                <span
                  style={{
                    fontFamily: fontMono,
                    fontSize: 10,
                    fontWeight: 600,
                    color: T.ink,
                  }}
                >
                  {r.pct}%
                </span>
              </div>
              <div
                className="mt-1 overflow-hidden"
                style={{
                  height: 3,
                  background: "#EAEDEF",
                  borderRadius: 999,
                }}
              >
                <div
                  style={{
                    width: active ? `${r.pct}%` : "0%",
                    height: "100%",
                    background: r.color,
                    borderRadius: 999,
                    transition: `width 900ms cubic-bezier(0.22, 1, 0.36, 1) ${delay + 200 + rows.indexOf(r) * 120}ms`,
                  }}
                />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function CalendarIllustration({ active = true, delay = 0 }) {
  const days = ["M", "T", "W", "T", "F", "S", "S"];
  const enter = {
    opacity: active ? 1 : 0,
    transform: active ? "translateY(0)" : "translateY(6px)",
    transition: `opacity 480ms ease ${delay}ms, transform 620ms cubic-bezier(0.22, 1, 0.36, 1) ${delay}ms`,
  };
  return (
    <div className="absolute inset-0 flex items-center justify-center p-5">
      <div
        style={{
          width: "100%",
          maxWidth: 240,
          background: "#FFFFFF",
          borderRadius: 10,
          border: `1px solid ${T.line}`,
          boxShadow: "0 8px 18px -12px rgba(11,18,32,0.10)",
          padding: 14,
          ...enter,
        }}
      >
        <div className="flex items-center justify-between">
          <div
            style={{
              fontFamily: fontMono,
              fontSize: 9,
              letterSpacing: "0.20em",
              fontWeight: 700,
              color: T.inkMuted,
              textTransform: "uppercase",
            }}
          >
            This Week
          </div>
          <div
            className="inline-flex items-center gap-1"
            style={{
              padding: "2px 6px",
              borderRadius: 999,
              background: "rgba(15,118,110,0.10)",
              color: T.accent,
              fontFamily: fontMono,
              fontSize: 8.5,
              letterSpacing: "0.16em",
              fontWeight: 700,
              textTransform: "uppercase",
            }}
          >
            <span
              style={{
                width: 5,
                height: 5,
                borderRadius: 999,
                background: T.accent,
                display: "inline-block",
              }}
            />
            New
          </div>
        </div>
        <div className="mt-3 grid grid-cols-7 gap-1">
          {days.map((d, i) => {
            const isMonday = i === 0;
            return (
              <div
                key={i}
                className="flex flex-col items-center gap-1"
              >
                <span
                  style={{
                    fontFamily: fontMono,
                    fontSize: 9,
                    fontWeight: 600,
                    color: isMonday ? T.accent : T.inkMuted,
                  }}
                >
                  {d}
                </span>
                <span
                  style={{
                    width: 22,
                    height: 22,
                    borderRadius: 999,
                    background: isMonday ? T.accent : "transparent",
                    color: isMonday ? "#FFFFFF" : T.ink,
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontFamily: fontSans,
                    fontSize: 10,
                    fontWeight: isMonday ? 600 : 500,
                    border: isMonday ? "none" : `1px solid ${T.line}`,
                    transform:
                      isMonday && !active ? "scale(0.6)" : "scale(1)",
                    opacity: isMonday && !active ? 0 : 1,
                    transition: isMonday
                      ? `transform 500ms cubic-bezier(0.22, 1, 0.36, 1) ${delay + 220}ms, opacity 400ms ease ${delay + 220}ms`
                      : "none",
                  }}
                >
                  {i + 3}
                </span>
              </div>
            );
          })}
        </div>
        <div
          className="mt-3 pt-3"
          style={{
            borderTop: `1px solid ${T.line}`,
            fontFamily: fontSans,
            fontSize: 11,
            fontWeight: 500,
            color: T.ink,
          }}
        >
          Match Monday · 2 new
        </div>
      </div>
    </div>
  );
}

function ConnectIllustration({ active = true, delay = 0 }) {
  const enter = {
    opacity: active ? 1 : 0,
    transform: active ? "translateY(0)" : "translateY(6px)",
    transition: `opacity 480ms ease ${delay}ms, transform 620ms cubic-bezier(0.22, 1, 0.36, 1) ${delay}ms`,
  };
  const MUTED_RED = "#B94A48";
  return (
    <div className="absolute inset-0 flex items-center justify-center p-5">
      <div
        style={{
          width: "100%",
          maxWidth: 240,
          background: "#FFFFFF",
          borderRadius: 10,
          border: `1px solid ${T.line}`,
          boxShadow: "0 8px 18px -12px rgba(11,18,32,0.10)",
          padding: 14,
          ...enter,
        }}
      >
        <div
          style={{
            fontFamily: fontMono,
            fontSize: 9,
            letterSpacing: "0.20em",
            fontWeight: 700,
            color: T.inkMuted,
            textTransform: "uppercase",
          }}
        >
          Your Move
        </div>
        <div
          className="mt-1.5"
          style={{
            fontFamily: fontSans,
            fontSize: 12,
            fontWeight: 500,
            color: T.ink,
            letterSpacing: "-0.005em",
            lineHeight: 1.3,
          }}
        >
          Darius, 35 · 84% Match
        </div>

        {/* Decision buttons */}
        <div className="mt-3 grid grid-cols-2 gap-1.5">
          {/* Connect — primary, pressed/active state */}
          <div
            className="flex items-center justify-center gap-1"
            style={{
              padding: "7px 8px",
              borderRadius: 6,
              background: T.accent,
              color: "#FFFFFF",
              fontFamily: fontSans,
              fontSize: 11,
              fontWeight: 600,
              letterSpacing: "-0.005em",
              boxShadow: "0 4px 10px -4px rgba(15,118,110,0.55)",
              transform: "translateY(-1px)",
            }}
          >
            <svg
              width="11"
              height="11"
              viewBox="0 0 24 24"
              fill="none"
              aria-hidden="true"
            >
              <path
                d="M20 6L9 17l-5-5"
                stroke="currentColor"
                strokeWidth="3.2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            Connect
          </div>

          {/* Not this time — secondary, muted red decline treatment */}
          <div
            className="flex items-center justify-center gap-1"
            style={{
              padding: "7px 8px",
              borderRadius: 6,
              background: "#FFFFFF",
              color: MUTED_RED,
              fontFamily: fontSans,
              fontSize: 11,
              fontWeight: 500,
              letterSpacing: "-0.005em",
              border: `1px solid ${MUTED_RED}`,
            }}
          >
            <svg
              width="10"
              height="10"
              viewBox="0 0 24 24"
              fill="none"
              aria-hidden="true"
            >
              <path
                d="M18 6L6 18M6 6l12 12"
                stroke="currentColor"
                strokeWidth="3"
                strokeLinecap="round"
              />
            </svg>
            Not now
          </div>
        </div>

        {/* Small cursor indicator hovering over the Connect button */}
        <div
          className="mt-2 flex items-center gap-1.5"
          style={{
            fontFamily: fontMono,
            fontSize: 8.5,
            letterSpacing: "0.16em",
            fontWeight: 700,
            color: T.accent,
            textTransform: "uppercase",
          }}
        >
          <span
            style={{
              width: 5,
              height: 5,
              borderRadius: 999,
              background: T.accent,
              display: "inline-block",
            }}
          />
          Your choice
        </div>
      </div>
    </div>
  );
}


// ── How-It-Works step grid — cohesive technical product sequence ───
// One consistent visual system across all four cards. The information
// inside each card provides the visual interest, not decorative
// container tints.
const HOW_STEPS = [
  {
    n: "01",
    title: "Complete the survey",
    body: "Tell us what you want, what matters to you, and where you have flexibility.",
  },
  {
    n: "02",
    title: "HAEVN evaluates compatibility",
    body: "We look for people who share your intent and clear the compatibility threshold across all five areas.",
  },
  {
    n: "03",
    title: "Receive your Match Monday status",
    body: "Your Match, Recommendation, or update arrives by text each Monday.",
  },
  {
    n: "04",
    title: "Decide what happens next",
    body: "When there's mutual interest, you choose whether to connect.",
  },
];

const HOW_CANVAS = "#F5F6F7";

function HowStepsGrid() {
  const rootRef = useRef(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const reduce = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    if (reduce) {
      setInView(true);
      return;
    }
    const node = rootRef.current;
    if (!node) return;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            setInView(true);
            io.disconnect();
          }
        });
      },
      { threshold: 0.2 }
    );
    io.observe(node);
    return () => io.disconnect();
  }, []);

  const illustrations = [
    <SurveyIllustration active={inView} />,
    <CompatIllustration active={inView} delay={140} />,
    <CalendarIllustration active={inView} delay={280} />,
    <ConnectIllustration active={inView} delay={420} />,
  ];

  return (
    <ol
      ref={rootRef}
      data-testid="how-steps"
      className="mt-12 md:mt-16 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 lg:gap-6"
    >
      {HOW_STEPS.map((step, i) => (
        <li
          key={step.n}
          data-testid={`how-step-${step.n}`}
          className="relative overflow-hidden"
          style={{
            background: "#FFFFFF",
            border: `1px solid ${T.line}`,
            borderRadius: 14,
            boxShadow: "0 1px 0 rgba(11,18,32,0.02)",
          }}
        >
          {/* Thin teal top rule — continuity across all four cards */}
          <div
            aria-hidden="true"
            style={{
              height: 2,
              background: T.accent,
            }}
          />
          {/* Product demonstration canvas */}
          <div
            className="relative"
            style={{
              background: HOW_CANVAS,
              height: 168,
              borderBottom: `1px solid ${T.line}`,
            }}
          >
            {illustrations[i]}
            <span
              className="absolute top-4 right-4"
              style={{
                fontFamily: fontMono,
                fontSize: 12,
                letterSpacing: "0.16em",
                fontWeight: 700,
                color: T.accent,
                lineHeight: 1,
              }}
            >
              {step.n}
            </span>
          </div>
          {/* Explanation area */}
          <div className="p-6 md:p-7 bg-white">
            <h3
              style={{
                fontFamily: fontSans,
                fontSize: 19,
                lineHeight: 1.25,
                letterSpacing: "-0.012em",
                fontWeight: 600,
                color: T.ink,
                margin: 0,
              }}
            >
              {step.title}
            </h3>
            <p
              className="mt-3"
              style={{
                fontFamily: fontSans,
                fontSize: 15,
                lineHeight: 1.55,
                color: T.inkMuted,
                margin: 0,
              }}
            >
              {step.body}
            </p>
          </div>
        </li>
      ))}
    </ol>
  );
}


const ORG_LOGO =
  "https://customer-assets.emergentagent.com/job_8e1914ff-fb25-4b50-9af6-080e8f865a43/artifacts/w9f850az_Asset%209%404x-8.png";

// Approved hero background (couple enjoying coffee at café — approved mockup, 2026-10-06).
const HERO_BG_SRC =
  "https://customer-assets-v7afamib.emergentagent.net/job_8e1914ff-fb25-4b50-9af6-080e8f865a43/artifacts/86a93ad7839bde14_affectionate-couple-enjoying-coffee-and-a-pastry-2026-09-22-18-33-36-utc.webp";
// Hero accent (teal) — client-approved substitution for the mockup's #D2AE80.
const HERO_ACCENT = "#008080";

// Approved city photograph backgrounds.
const CITY_BG = {
  "austin-tx":
    "https://customer-assets.emergentagent.com/job_8e1914ff-fb25-4b50-9af6-080e8f865a43/artifacts/lkym1i0w_Austin2.jpg",
  "portland-or":
    "https://customer-assets.emergentagent.com/job_8e1914ff-fb25-4b50-9af6-080e8f865a43/artifacts/417obtvv_Portland.jpg",
  "tampa-st-pete-fl":
    "https://customer-assets.emergentagent.com/job_8e1914ff-fb25-4b50-9af6-080e8f865a43/artifacts/boi1mn8o_Tampa.jpg",
};

// ── Section surfaces — alternating white / light grey pattern ──────
// White foundation (#FFFFFF) alternates with neutral surface (#F4F4F5)
// from the token palette across the six below-hero sections, ending
// on grey before the untouched dark footer.
const SECTION_BG = {
  white: "#FFFFFF",
  grey: "#F4F4F5",
};

const GOLD = "#E6A83A";

// ── JSON-LD (unchanged) ─────────────────────────────────────────────
const ORGANIZATION_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "Organization",
  name: "HAEVN",
  legalName: "HAEVN",
  url: "https://haevn.co",
  logo: ORG_LOGO,
  description:
    "HAEVN is modern, technology-enabled matchmaking. Define what you want, receive verified matches every Monday, and connect only when both members qualify.",
  founder: { "@type": "Person", name: "Rik Foote" },
  sameAs: [
    "https://www.instagram.com/haevn.co",
    "https://www.linkedin.com/company/haevn",
  ],
};
const WEBSITE_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  name: "HAEVN",
  url: "https://haevn.co",
  publisher: { "@type": "Organization", name: "HAEVN" },
  potentialAction: {
    "@type": "SearchAction",
    target: "https://haevn.co/cities?q={search_term_string}",
    "query-input": "required name=search_term_string",
  },
};

// ── Type scales scoped to this page ─────────────────────────────────
// Reduced scales per the 2026-02-28 design brief.
const eyebrow = {
  fontFamily: fontMono,
  fontSize: 12,
  letterSpacing: "0.14em",
  textTransform: "uppercase",
  fontWeight: 600,
  color: T.accent, // teal — used for labels & structural accents
};
const eyebrowMuted = { ...eyebrow, color: T.inkMuted };

const monoSupport = {
  fontFamily: fontMono,
  fontSize: 12,
  letterSpacing: "0.06em",
  fontWeight: 500,
  textTransform: "uppercase",
  color: T.inkMuted,
};

// Reduced H2 scale — max 32px desktop, ~24px mobile.
const h2 = {
  fontFamily: fontSans,
  fontSize: "clamp(1.5rem, 1.9vw, 2rem)",
  lineHeight: 1.1,
  letterSpacing: "-0.025em",
  fontWeight: 500,
  color: T.ink,
  margin: 0,
};

const body = {
  fontFamily: fontSans,
  fontSize: 17,
  lineHeight: 1.65,
  color: T.ink,
  fontWeight: 400,
};
const bodyMuted = { ...body, color: T.inkMuted };

// ── Contextual link (teal underline) ────────────────────────────────
function ContextLink({ to, children, testId, tone = "ink" }) {
  const color = tone === "light" ? "#FFFFFF" : T.accent;
  return (
    <Link
      to={to}
      data-testid={testId}
      style={{
        fontFamily: fontMono,
        fontSize: 12,
        letterSpacing: "0.12em",
        textTransform: "uppercase",
        fontWeight: 600,
        color,
        borderBottom: `1px solid ${color}`,
        paddingBottom: 2,
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
      }}
    >
      {children}
      <ArrowUpRight className="w-3.5 h-3.5" strokeWidth={2} />
    </Link>
  );
}

// ── Persona story cross-fade (unchanged assets) ─────────────────────
function PersonaStoryCarousel() {
  const [activeIdx, setActiveIdx] = useState(0);

  useEffect(() => {
    if (PERSONA_STORIES.length <= 1) return;
    const id = setInterval(() => {
      setActiveIdx((i) => (i + 1) % PERSONA_STORIES.length);
    }, STORY_INTERVAL_MS);
    return () => clearInterval(id);
  }, []);

  return (
    <div
      data-testid="persona-story-carousel"
      className="relative w-full overflow-hidden bg-[#0B1220] mx-auto"
      style={{ borderRadius: 12, aspectRatio: "1 / 1", maxWidth: 560 }}
    >
      {PERSONA_STORIES.map((s, i) => (
        <img
          key={s.src}
          src={s.src}
          alt={s.alt}
          loading="lazy"
          decoding="async"
          className="absolute inset-0 h-full w-full object-cover will-change-[opacity]"
          style={{
            opacity: i === activeIdx ? 1 : 0,
            transition: `opacity ${STORY_FADE_MS}ms ease-in-out`,
          }}
        />
      ))}
      {PERSONA_STORIES.length > 1 && (
        <div className="absolute bottom-5 left-1/2 -translate-x-1/2 flex items-center gap-2">
          {PERSONA_STORIES.map((_, i) => (
            <span
              key={i}
              aria-hidden="true"
              className="h-1.5 rounded-full transition-all duration-300"
              style={{
                width: i === activeIdx ? "20px" : "6px",
                backgroundColor:
                  i === activeIdx
                    ? "rgba(255,255,255,0.95)"
                    : "rgba(255,255,255,0.45)",
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ── City status chip (strong statuses per brief) ────────────────────
function CityStatusChip({ variant, onDark = false }) {
  const map = {
    live: { label: "Live", fill: T.accent, text: "#FFFFFF" },
    building: { label: "Now Building" },
    coming: { label: "Coming Soon" },
  };
  if (variant === "live") {
    return (
      <span
        data-testid="home-city-status-live"
        style={{
          fontFamily: fontMono,
          fontSize: 10,
          letterSpacing: "0.16em",
          textTransform: "uppercase",
          fontWeight: 600,
          padding: "4px 10px",
          background: T.accent,
          color: "#FFFFFF",
          borderRadius: 4,
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
          whiteSpace: "nowrap",
        }}
      >
        <span
          aria-hidden="true"
          style={{
            width: 6,
            height: 6,
            borderRadius: 999,
            background: "#FFFFFF",
            display: "inline-block",
          }}
        />
        {map.live.label}
      </span>
    );
  }
  const color = onDark ? "#FFFFFF" : T.accent;
  return (
    <span
      data-testid={`home-city-status-${variant}`}
      style={{
        fontFamily: fontMono,
        fontSize: 10,
        letterSpacing: "0.16em",
        textTransform: "uppercase",
        fontWeight: 600,
        padding: "4px 10px",
        border: `1px solid ${color}`,
        color,
        background: onDark ? "rgba(5,8,16,0.35)" : "transparent",
        backdropFilter: onDark ? "blur(4px)" : "none",
        borderRadius: 4,
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        whiteSpace: "nowrap",
      }}
    >
      <span
        aria-hidden="true"
        style={{
          width: 6,
          height: 6,
          borderRadius: 999,
          border: `1.5px solid ${color}`,
          display: "inline-block",
        }}
      />
      {map[variant].label}
    </span>
  );
}

// ── Dynamic Match Monday date helper ────────────────────────────────
// Returns the most recent Monday, formatted "MMM D" (e.g., "AUG 10").
// If today is Monday, returns today's date. Otherwise the immediately
// preceding Monday.
function getLastMatchMonday(now = new Date()) {
  const d = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const day = d.getDay(); // 0=Sun,1=Mon,...,6=Sat
  const daysSinceMonday = day === 0 ? 6 : day - 1;
  d.setDate(d.getDate() - daysSinceMonday);
  const months = ["JAN","FEB","MAR","APR","MAY","JUN","JUL","AUG","SEP","OCT","NOV","DEC"];
  return `${months[d.getMonth()]} ${d.getDate()}`;
}

// ── City tile with photograph background ────────────────────────────
function CityCard({ slug, name, state, variant, description, exploreHref, matchesCount }) {
  const bg = CITY_BG[slug];
  // 2026-02: "live" and "plain" both render as full local-activity cards.
  // The chip is only rendered for the explicit "live" variant; "plain"
  // suppresses it since HAEVN no longer surfaces city activation state.
  const showChip = variant === "live";
  const showLocalActivity = variant === "live" || variant === "plain";
  const linkOnDark = Boolean(bg);
  // Per-city match count for the LIVE variant. Explicit prop wins;
  // otherwise fall back to the slug-specific default so a marketing
  // edit is a single-source change at the call site.
  const CITY_MATCH_COUNTS = {
    "austin-tx": "500+",
    "portland-or": "300+",
    "tampa-st-pete-fl": "100+",
  };
  const matchesLabel = matchesCount || CITY_MATCH_COUNTS[slug] || "500+";
  return (
    <article
      data-testid={`home-city-card-${slug}`}
      className="relative flex flex-col overflow-hidden"
      style={{
        borderRadius: 12,
        minHeight: 360,
        background: bg ? "#0B1220" : T.bg,
      }}
    >
      {bg && (
        <>
          <img
            src={bg}
            alt=""
            aria-hidden="true"
            loading="lazy"
            decoding="async"
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div
            aria-hidden="true"
            className="absolute inset-0"
            style={{
              background:
                "linear-gradient(180deg, rgba(5,8,16,0.05) 0%, rgba(5,8,16,0.15) 35%, rgba(5,8,16,0.62) 68%, rgba(5,8,16,0.94) 100%)",
            }}
          />
        </>
      )}
      <div className="relative flex flex-col h-full p-6 md:p-7">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          {showChip ? (
            <CityStatusChip variant={variant} onDark={Boolean(bg)} />
          ) : (
            <span aria-hidden="true" />
          )}
          <span
            style={{
              ...eyebrowMuted,
              fontSize: 11,
              color: bg ? "rgba(255,255,255,0.75)" : T.inkMuted,
            }}
          >
            {state}
          </span>
        </div>
        <div className="mt-auto pt-24">
          <h3
            style={{
              fontFamily: fontSans,
              fontSize: "clamp(24px, 2.4vw, 30px)",
              lineHeight: 1.05,
              letterSpacing: "-0.02em",
              fontWeight: 500,
              color: bg ? "#FFFFFF" : T.ink,
              margin: 0,
            }}
          >
            {name}
          </h3>

          {showLocalActivity ? (
            <div
              data-testid={`home-city-activity-${slug}`}
              className="mt-3"
            >
              <div
                style={{
                  fontFamily: fontMono,
                  fontSize: 10.5,
                  letterSpacing: "0.18em",
                  textTransform: "uppercase",
                  fontWeight: 600,
                  color: bg
                    ? "rgba(255,255,255,0.72)"
                    : T.inkMuted,
                  lineHeight: 1.3,
                }}
              >
                Last Match Monday · {getLastMatchMonday()}
              </div>
              <div className="mt-2 flex items-baseline gap-2 flex-wrap">
                <span
                  style={{
                    fontFamily: fontSans,
                    fontSize: "clamp(22px, 1.9vw, 26px)",
                    fontWeight: 600,
                    letterSpacing: "-0.018em",
                    lineHeight: 1,
                    color: bg ? "#FFFFFF" : T.ink,
                  }}
                >
                  {matchesLabel}
                </span>
                <span
                  style={{
                    fontFamily: fontSans,
                    fontSize: 14,
                    fontWeight: 400,
                    lineHeight: 1.35,
                    color: bg ? "rgba(255,255,255,0.88)" : T.ink,
                  }}
                >
                  Matches &amp; Recommendations Delivered
                </span>
              </div>
            </div>
          ) : (
            <p
              className="mt-2"
              style={{
                fontFamily: fontSans,
                fontSize: 15,
                lineHeight: 1.5,
                color: bg ? "rgba(255,255,255,0.85)" : T.inkMuted,
                margin: 0,
                maxWidth: "34ch",
              }}
            >
              {description}
            </p>
          )}

          {exploreHref && (
            <Link
              to={exploreHref}
              data-testid={`home-city-explore-${slug}`}
              className="mt-5 inline-flex items-center gap-1.5 transition-opacity duration-200 hover:opacity-80"
              style={{
                fontFamily: fontMono,
                fontSize: 12,
                letterSpacing: "0.14em",
                textTransform: "uppercase",
                fontWeight: 600,
                color: linkOnDark ? "#FFFFFF" : T.ink,
                borderBottom: `1px solid ${linkOnDark ? "rgba(255,255,255,0.85)" : T.ink}`,
                paddingBottom: 2,
                width: "fit-content",
              }}
            >
              Explore {name}
              <ArrowUpRight className="w-3.5 h-3.5" strokeWidth={2} />
            </Link>
          )}
        </div>
      </div>
    </article>
  );
}

// ── Product-proof visual replaced by DariusMatchCard (imported above) ─

// ── Closing CTA — dark navy, flows into footer ──────────────────────
function ClosingCta({ surveyUrl }) {
  const rootRef = useRef(null);
  const [inView, setInView] = useState(false);
  const [arrowHover, setArrowHover] = useState(false);

  useEffect(() => {
    const reduce = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    if (reduce) {
      setInView(true);
      return;
    }
    const node = rootRef.current;
    if (!node) return;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            setInView(true);
            io.disconnect();
          }
        });
      },
      { threshold: 0.25 }
    );
    io.observe(node);
    return () => io.disconnect();
  }, []);

  const step = (i) => ({
    opacity: inView ? 1 : 0,
    transform: inView ? "translateY(0)" : "translateY(10px)",
    transition: `opacity 520ms ease ${i * 140}ms, transform 620ms cubic-bezier(0.22, 1, 0.36, 1) ${i * 140}ms`,
  });

  return (
    <section
      ref={rootRef}
      data-testid="home-final-cta-section"
      style={{ background: T.ink, color: "#FFFFFF" }}
    >
      <div className="mx-auto max-w-7xl px-6 md:px-10 py-20 md:py-28 lg:py-32">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start lg:items-center">
          {/* Left column — eyebrow + dominant headline */}
          <div className="lg:col-span-7">
            <p
              data-testid="final-cta-eyebrow"
              style={{
                fontFamily: fontMono,
                fontSize: 12,
                letterSpacing: "0.20em",
                textTransform: "uppercase",
                fontWeight: 600,
                color: T.accent,
                margin: 0,
                ...step(0),
              }}
            >
              Ready when you are
            </p>
            <h2
              data-testid="final-cta-headline"
              className="mt-6"
              style={{
                fontFamily: fontSans,
                fontSize: "clamp(36px, 5.2vw, 60px)",
                lineHeight: 1.02,
                letterSpacing: "-0.028em",
                fontWeight: 500,
                color: "#FFFFFF",
                margin: 0,
                maxWidth: "20ch",
                ...step(1),
              }}
            >
              You don&rsquo;t need to search.
              <br />
              <span style={{ color: T.accent }}>
                You just need to be findable.
              </span>
            </h2>
          </div>

          {/* Right column — compact action area */}
          <div
            className="lg:col-span-5"
            data-testid="final-cta-action"
            style={{ ...step(2) }}
          >
            <p
              style={{
                fontFamily: fontSans,
                fontSize: 16,
                lineHeight: 1.55,
                color: "rgba(255,255,255,0.75)",
                margin: 0,
                maxWidth: "46ch",
              }}
            >
              Tell us who you are, what you&rsquo;re looking for, and what matters to you. We&rsquo;ll take it from there.
            </p>
            <a
              href={surveyUrl}
              onClick={() => trackSurveyStart("homepage_final_cta")}
              onMouseEnter={() => setArrowHover(true)}
              onMouseLeave={() => setArrowHover(false)}
              className="mt-7 w-full sm:w-auto"
              data-testid="final-cta-btn"
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 10,
                padding: "16px 28px",
                background: "#FFFFFF",
                color: T.ink,
                borderRadius: 6,
                fontFamily: fontSans,
                fontSize: 15,
                fontWeight: 600,
                letterSpacing: "-0.005em",
                textDecoration: "none",
                transition: "background-color 200ms ease",
              }}
              onMouseOver={(e) => { e.currentTarget.style.background = "#F1F1F0"; }}
              onMouseOut={(e) => { e.currentTarget.style.background = "#FFFFFF"; }}
            >
              Join the Network
              <ArrowRight
                className="w-4 h-4"
                strokeWidth={2}
                style={{
                  transform: arrowHover ? "translateX(4px)" : "translateX(0)",
                  transition: "transform 220ms ease",
                }}
              />
            </a>
            <div
              className="mt-5"
              style={{
                fontFamily: fontMono,
                fontSize: 11.5,
                letterSpacing: "0.16em",
                textTransform: "uppercase",
                fontWeight: 600,
                color: "rgba(255,255,255,0.60)",
              }}
            >
              Free&nbsp;·&nbsp;About 15 minutes&nbsp;·&nbsp;No app required
            </div>
          </div>
        </div>
      </div>

      {/* Thin divider bridging CTA into the footer */}
      <div
        aria-hidden="true"
        className="mx-auto max-w-7xl px-6 md:px-10"
      >
        <div style={{ height: 1, background: "rgba(255,255,255,0.08)" }} />
      </div>
    </section>
  );
}

export default function HomePage() {
  const SURVEY_URL = useSurveyUrl();

  return (
    <div className="min-h-screen bg-white" data-testid="home-page">
      <SEO
        title="HAEVN | Modern Matchmaking"
        path="/"
        description="HAEVN is modern, technology-enabled matchmaking. Tell HAEVN what you want, receive a Match Monday status each week, and decide together when to connect."
      />
      <JsonLd data={[ORGANIZATION_SCHEMA, WEBSITE_SCHEMA]} />
      <Navigation variant="dark" />

      {/* ── HERO (approved composition; refined breathing) ─────────── */}
      <section
        className="relative overflow-hidden text-white"
        data-testid="hero-section"
        style={{
          background: "#050810",
          fontFamily: "'Geist', -apple-system, BlinkMacSystemFont, 'Segoe UI', Helvetica, sans-serif",
        }}
      >
        {/* ── MOBILE-ONLY: couple photo above the text ─────────────────
            Photo sits full-bleed at the top. A dark gradient at the
            bottom edge dissolves the image into the dark navy text
            block below (per mockup). */}
        <div
          className="md:hidden relative w-full overflow-hidden"
          style={{ height: 440 }}
          aria-hidden="true"
        >
          <img
            src={HERO_BG_SRC}
            alt=""
            loading="eager"
            fetchPriority="high"
            decoding="async"
            data-testid="hero-bg-image-mobile"
            className="absolute inset-0 w-full h-full object-cover object-[60%_center]"
          />
          {/* Soft bottom fade so the image dissolves into the dark
              text block instead of ending on a hard seam. */}
          <div
            className="absolute inset-x-0 bottom-0 pointer-events-none"
            style={{
              height: 140,
              background:
                "linear-gradient(to top, rgba(5,8,16,1) 0%, rgba(5,8,16,0.85) 35%, rgba(5,8,16,0.45) 65%, rgba(5,8,16,0) 100%)",
            }}
          />
        </div>

        {/* ── DESKTOP-ONLY: couple photo on the RIGHT side only ─────
            In the approved mockup the image does not span the full
            viewport; it occupies roughly the right 65% while the left
            is pure dark navy. We mirror that here so both subjects
            are visible and nothing is obscured by the headline. */}
        <div
          aria-hidden="true"
          className="hidden md:block absolute top-0 bottom-0 right-0 overflow-hidden"
          style={{ left: "32%" }}
        >
          <img
            src={HERO_BG_SRC}
            alt=""
            loading="eager"
            fetchPriority="high"
            decoding="async"
            data-testid="hero-bg-image"
            className="absolute inset-0 w-full h-full object-cover object-[center_center]"
          />
          {/* Slight uniform mute over the whole image so overlapping
              headline/body copy remains legible without altering the
              approved left-edge gradient below. */}
          <div
            aria-hidden="true"
            className="absolute inset-0 pointer-events-none"
            style={{ background: "rgba(5,8,16,0.22)" }}
          />
          {/* Soft left-edge fade so the photo melts into the dark navy
              block instead of ending on a hard seam. */}
          <div
            className="absolute inset-y-0 left-0 pointer-events-none"
            style={{
              width: 220,
              background:
                "linear-gradient(90deg, rgba(5,8,16,1) 0%, rgba(5,8,16,0.65) 45%, rgba(5,8,16,0) 100%)",
            }}
          />
        </div>

        {/*
          Content column.
          - Mobile: sits below the stacked image in normal flow. No
            overlay — solid dark navy from the parent section.
          - Desktop: absolutely positioned via min-height + inset;
            text overlays the image on the left.
        */}
        <div
          className="relative max-w-7xl mx-auto w-full px-6 md:px-10 md:flex md:flex-col md:justify-center"
          style={{ ["--desktop-min-h"]: "min(100vh, 900px)" }}
        ><div
            className="pt-6 md:pt-0 pb-10 md:pb-0 md:min-h-[var(--desktop-min-h)] md:flex md:items-center"
          >
            <div className="max-w-[560px] md:max-w-[620px] lg:max-w-[660px]">
              <h1
                data-testid="hero-headline"
                className="m-0 text-[44px] leading-[1.03] md:text-[clamp(2.5rem,4.2vw,3.625rem)] md:leading-[1.04]"
                style={{
                  fontFamily: "'Geist', -apple-system, BlinkMacSystemFont, 'Segoe UI', Helvetica, sans-serif",
                  fontWeight: 600,
                  letterSpacing: "-0.035em",
                  color: "#FFFFFF",
                }}
              >
                There&rsquo;s someone
                <br />
                worth meeting.
                <br />
                <span style={{ color: HERO_ACCENT, fontWeight: 600 }}>
                  We&rsquo;ll find them for you.
                </span>
              </h1>

              <p
                data-testid="hero-subhead"
                className="mt-5 md:mt-7"
                style={{
                  fontFamily: "'Geist', -apple-system, BlinkMacSystemFont, 'Segoe UI', Helvetica, sans-serif",
                  fontSize: "clamp(16px, 1.2vw, 18px)",
                  fontWeight: 400,
                  lineHeight: 1.5,
                  color: "rgba(255,255,255,0.82)",
                  maxWidth: "38ch",
                }}
              >
                Tell us who you are and what you&rsquo;re looking for. HAEVN does the searching and introduces you to highly compatible people.
              </p>

              <div className="mt-7 md:mt-9 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
                <a
                  href={SURVEY_URL}
                  data-testid="hero-cta"
                  onClick={() => trackSurveyStart("hero_primary")}
                  className="inline-flex items-center justify-center gap-2 transition-colors duration-200 w-full sm:w-auto"
                  style={{
                    fontFamily: "'Geist', -apple-system, BlinkMacSystemFont, 'Segoe UI', Helvetica, sans-serif",
                    fontSize: 15,
                    fontWeight: 500,
                    borderRadius: 6,
                    background: "#FFFFFF",
                    color: "#0B1220",
                    padding: "14px 24px",
                    letterSpacing: 0,
                  }}
                  onMouseOver={(e) => { e.currentTarget.style.background = "#E5E7EB"; }}
                  onMouseOut={(e) => { e.currentTarget.style.background = "#FFFFFF"; }}
                >
                  Join the Network
                  <ArrowRight className="w-4 h-4" aria-hidden="true" />
                </a>
                <Link
                  to="/how-it-works"
                  data-testid="hero-secondary-cta"
                  className="inline-flex items-center justify-center gap-2 transition-colors duration-200 w-full sm:w-auto"
                  style={{
                    fontFamily: "'Geist', -apple-system, BlinkMacSystemFont, 'Segoe UI', Helvetica, sans-serif",
                    fontSize: 15,
                    fontWeight: 500,
                    borderRadius: 6,
                    background: "transparent",
                    color: "#FFFFFF",
                    border: "1px solid rgba(255,255,255,0.55)",
                    padding: "13px 24px",
                  }}
                  onMouseOver={(e) => { e.currentTarget.style.background = "rgba(255,255,255,0.08)"; e.currentTarget.style.borderColor = "#FFFFFF"; }}
                  onMouseOut={(e) => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.borderColor = "rgba(255,255,255,0.55)"; }}
                >
                  See How It Works
                  <ArrowRight className="w-4 h-4" aria-hidden="true" />
                </Link>
              </div>

              <div
                className="mt-6 sm:mt-7"
                style={{
                  fontFamily: "'Geist Mono', ui-monospace, SFMono-Regular, Menlo, monospace",
                  fontSize: 12,
                  letterSpacing: "0.14em",
                  fontWeight: 500,
                  textTransform: "uppercase",
                  color: "rgba(255,255,255,0.55)",
                }}
              >
                Free&nbsp;·&nbsp;About 15 minutes&nbsp;·&nbsp;No app required
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── 2. MODERN MATCHMAKING (white) ──────────────────────────── */}
      <section
        data-testid="home-category-section"
        style={{ background: SECTION_BG.white }}
      >
        <div className="mx-auto max-w-7xl px-6 md:px-10 py-14 md:py-24">
          <div className="grid grid-cols-1 lg:grid-cols-[46fr_54fr] gap-10 lg:gap-14 items-center">
            <div>
              <p data-testid="category-eyebrow" style={eyebrow}>
                Modern Matchmaking
              </p>
              <h2
                data-testid="category-headline"
                className="mt-4"
                style={{ ...h2, maxWidth: "30ch" }}
              >
                Matchmaking used to be a luxury.
                <br />
                Technology changed that.
              </h2>
              <p className="mt-6" style={{ ...body, maxWidth: "60ch" }}>
                Tell HAEVN what you&rsquo;re looking for. We find compatible people in your city and make the introduction.
              </p>
              <p className="mt-4" style={{ ...bodyMuted, maxWidth: "60ch" }}>
                No swiping. No searching. No dating app grind.
              </p>
              <div className="mt-7">
                <ContextLink to="/how-it-works" testId="category-link-hiw">
                  See how HAEVN works
                </ContextLink>
              </div>
            </div>

            <div className="w-full">
              <div
                data-testid="category-video"
                className="relative w-full aspect-video overflow-hidden bg-[#0B1220]"
                style={{
                  borderRadius: 14,
                  boxShadow:
                    "0 1px 0 rgba(11,18,32,0.05), 0 30px 60px -30px rgba(11,18,32,0.28)",
                }}
              >
                <video
                  src="https://customer-assets.emergentagent.com/job_8e1914ff-fb25-4b50-9af6-080e8f865a43/artifacts/fwhwpl94_What%20is%20HAEVN.mp4#t=2"
                  controls
                  playsInline
                  preload="metadata"
                  className="absolute inset-0 h-full w-full object-cover"
                  aria-label="What is HAEVN — explainer video"
                >
                  Sorry, your browser doesn&rsquo;t support embedded videos.
                </video>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── 3. A BETTER REASON TO MEET (grey) ──────────────────────── */}
      <section
        data-testid="home-reason-section"
        style={{ background: SECTION_BG.grey }}
      >
        <div className="mx-auto max-w-7xl px-6 md:px-10 py-14 md:py-24">
          <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(0,420px)] gap-10 lg:gap-16 items-center">
            <div>
              <p data-testid="reason-eyebrow" style={eyebrow}>
                A Better Reason to Meet
              </p>
              <h2
                data-testid="reason-headline"
                className="mt-4"
                style={{ ...h2, maxWidth: "22ch" }}
              >
                An introduction should be based on more than a photo.
              </h2>
              <p className="mt-6" style={{ ...body, maxWidth: "56ch" }}>
                Attraction matters. But it shouldn&rsquo;t be the only reason two people meet. HAEVN introduces people only when both qualify and the Match Score reaches at least 80%.
              </p>
              <div className="mt-7">
                <ContextLink to="/how-80-works" testId="reason-link-80">
                  How 80% works
                </ContextLink>
              </div>
            </div>
            <div className="w-full">
              <DariusMatchCard />
            </div>
          </div>
        </div>
      </section>

      {/* ── 4. HOW IT WORKS (white) ────────────────────────────────── */}
      <section
        data-testid="home-how-section"
        style={{ background: SECTION_BG.white }}
      >
        <div className="mx-auto max-w-7xl px-6 md:px-10 py-14 md:py-24">
          {/* Intro copy — centered on desktop, left on mobile */}
          <div className="max-w-3xl">
            <p data-testid="how-eyebrow" style={eyebrow}>
              How It Works
            </p>
            <h2
              data-testid="how-headline"
              className="mt-4"
              style={{ ...h2, maxWidth: "22ch" }}
            >
              Tell us what matters.{" "}
              <span style={{ color: T.accent }}>
                We&rsquo;ll take it from there.
              </span>
            </h2>
            <p
              className="mt-6"
              style={{
                ...body,
                maxWidth: "58ch",
              }}
            >
              A modern matchmaking process built around intent, alignment, and respect.
            </p>
          </div>

          {/* Four illustrated steps */}
          <HowStepsGrid />

          {/* Closing row */}
          <div
            data-testid="how-closing"
            className="mt-12 md:mt-16 grid grid-cols-1 md:grid-cols-[minmax(0,1.5fr)_auto] gap-6 md:gap-10 items-end"
          >
            <p
              style={{
                fontFamily: fontSans,
                fontStyle: "italic",
                fontWeight: 400,
                fontSize: "clamp(17px, 1.6vw, 20px)",
                lineHeight: 1.4,
                letterSpacing: "-0.005em",
                color: T.ink,
                margin: 0,
                maxWidth: "58ch",
              }}
            >
              No feed to manage. No profiles to search. Just thoughtful introductions when the right alignment exists.
            </p>
            <div>
              <ContextLink to="/how-it-works" testId="how-link-full">
                Explore the full process
              </ContextLink>
            </div>
          </div>
        </div>
      </section>

      {/* ── 5. SHARED INTENT (grey) ────────────────────────────────── */}
      <section
        data-testid="home-intent-section"
        style={{ background: SECTION_BG.grey }}
      >
        <div className="mx-auto max-w-7xl px-6 md:px-10 py-14 md:py-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center">
            <div className="lg:col-span-5">
              <p data-testid="intent-eyebrow" style={eyebrow}>
                Shared Intent Comes First
              </p>
              <h2
                data-testid="intent-headline"
                className="mt-4"
                style={{ ...h2, maxWidth: "22ch" }}
              >
                The right match starts with wanting the same thing.
              </h2>
              <p className="mt-6" style={{ ...body, maxWidth: "56ch" }}>
                HAEVN doesn&rsquo;t ask everyone to want the same kind of relationship. It introduces people who are looking for compatible ones.
              </p>
              <div
                data-testid="intent-tags"
                className="mt-6 flex flex-wrap gap-2"
              >
                {[
                  "Long-term partnership",
                  "Casual dating",
                  "Friends with benefits",
                  "Ethical non-monogamy",
                  "Exploration",
                ].map((t) => (
                  <span
                    key={t}
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      padding: "6px 12px",
                      border: `1px solid ${T.accent}`,
                      color: T.accent,
                      borderRadius: 999,
                      fontFamily: fontSans,
                      fontSize: 13,
                      fontWeight: 500,
                      lineHeight: 1,
                    }}
                  >
                    {t}
                  </span>
                ))}
              </div>
            </div>

            <div className="lg:col-span-7 w-full">
              <PersonaStoryCarousel />
            </div>
          </div>
        </div>
      </section>

      {/* ── 6. CITIES (white) ──────────────────────────────────────── */}
      {/* 2026-02: Cities directory retired. HAEVN is no longer gated
          by city activation. This section is reframed to feature our
          city-specific SEO landings (Austin, Portland) without implying
          membership requires a launch. Status chips (Live / Now Building
          / Coming Soon) have been removed per user directive. */}
      <section
        data-testid="home-cities-section"
        style={{ background: SECTION_BG.white }}
      >
        <div className="mx-auto max-w-7xl px-6 md:px-10 py-14 md:py-24">
          <div className="grid grid-cols-1 md:grid-cols-[minmax(0,1fr)_auto] gap-6 md:gap-10 md:items-end">
            <div>
              <p data-testid="cities-eyebrow" style={eyebrow}>
                HAEVN near you
              </p>
              <h2
                data-testid="cities-headline"
                className="mt-4"
                style={{ ...h2, maxWidth: "22ch" }}
              >
                Modern matchmaking, built locally.
              </h2>
              <p className="mt-4" style={{ ...bodyMuted, maxWidth: "56ch" }}>
                HAEVN is growing nationally. Membership is open regardless of where you live, and local snapshots show how the network is forming around you.
              </p>
            </div>
          </div>

          <div className="mt-10 md:mt-12 grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5">
            <CityCard
              slug="austin-tx"
              name="Austin"
              state="TX"
              variant="plain"
              exploreHref="/cities/austin"
            />
            <CityCard
              slug="portland-or"
              name="Portland"
              state="OR"
              variant="plain"
              exploreHref="/cities/portland"
            />
          </div>
        </div>
      </section>

      {/* ── 7. CLOSING CTA — dark navy, flows directly into footer ── */}
      <ClosingCta surveyUrl={SURVEY_URL} />

      <Footer />
    </div>
  );
}
